import { yupResolver } from '@hookform/resolvers/yup';
import { buttonBaseClasses, inputBaseClasses, svgIconClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { Control, FieldPath, FieldValues, useForm } from 'react-hook-form';
import * as yup from 'yup';

import DateInput from '~components/FormFields/v2/DateInput';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TimeInput from '~components/FormFields/v2/TimeInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';

export type JobSummaryEditFormHandler = {
  onSubmit: () => Promise<
    | {
        loggingStartedAt: Dayjs | null;
        loggingEndedAt: Dayjs | null;
        approvedWorkTimeMinutes: number | null;
      }
    | undefined
  >;
  prefill: (values: {
    loggingStartedAt: Dayjs | null;
    loggingEndedAt: Dayjs | null;
    approvedWorkTimeHours: number | null;
  }) => void;
};

const enteredDateIsValid = (date: Dayjs | Date | null) => {
  if (!date) return false;
  if (dayjs.isDayjs(date)) {
    return date.isValid();
  }
  if (date instanceof Date) {
    return !isNaN(date.getTime());
  }
  return false;
};

const mergeDateAndTime = (date: Dayjs, time: Dayjs) => {
  return date.set('hour', time.hour()).set('minute', time.minute()).set('second', 0);
};

const jobSummaryEditSchema = yup.object().shape({
  loggingStartedAtTime: yup.date().notRequired(),
  loggingStartedAtDate: yup.date().notRequired(),
  loggingEndedAtTime: yup.date().notRequired(),
  loggingEndedAtDate: yup.date().notRequired(),
  approvedWorkTimeHours: yup.lazy((value) => {
    const isEmpty = isNil(value) || !String(value).trim().length;

    if (isEmpty) {
      return yup.string().notRequired();
    }

    return yup.number().min(0).required();
  }),
});
type JobSummaryEditDto = yup.InferType<typeof jobSummaryEditSchema>;

interface JobSummaryEditFormProps {
  approvedWorkTimeMinutes: number | null;
  approvedWorkTimeMinutesText: string;
  isSubmittingForm?: boolean;
  loggingEndedAt: Dayjs | null;
  loggingEndedAtText: string;
  loggingStartedAt: Dayjs | null;
  loggingStartedAtText: string;
  // Opt-in: hide the manual Total Hrs input and adopt the calculated total from
  // Start/End as they change (an external headline shows the total instead).
  hideTotalHoursInput?: boolean;
  onTotalHoursChange?: (hours: number | null) => void;
  // Opt-in: render field labels in the newer bold-caption style (10px/700/treadBlack,
  // matching the v2 FormFields Label used across the ticket drawer) instead of the
  // legacy gray body2 label. Also switches Start/End to sentence case — the redesign
  // surfaces spell them "Start"/"End" while the legacy card keeps "START"/"END".
  captionLabels?: boolean;
}

const JobSummaryEditForm = forwardRef<JobSummaryEditFormHandler, JobSummaryEditFormProps>(
  function JobSummaryEditForm(
    {
      approvedWorkTimeMinutes,
      approvedWorkTimeMinutesText,
      isSubmittingForm,
      loggingEndedAt,
      loggingEndedAtText,
      loggingStartedAt,
      loggingStartedAtText,
      hideTotalHoursInput,
      onTotalHoursChange,
      captionLabels,
    },
    ref,
  ) {
    const {
      clearErrors,
      control,
      formState: { dirtyFields },
      handleSubmit,
      setError,
      setValue,
      watch,
    } = useForm<JobSummaryEditDto>({
      resolver: yupResolver(jobSummaryEditSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        loggingStartedAtTime: loggingStartedAt
          ? dayjs.tz(loggingStartedAt).toDate()
          : null,
        loggingStartedAtDate: loggingStartedAt
          ? dayjs.tz(loggingStartedAt).toDate()
          : null,
        loggingEndedAtTime: loggingEndedAt ? dayjs.tz(loggingEndedAt).toDate() : null,
        loggingEndedAtDate: loggingEndedAt ? dayjs.tz(loggingEndedAt).toDate() : null,
        approvedWorkTimeHours: isNil(approvedWorkTimeMinutes)
          ? ''
          : Number((approvedWorkTimeMinutes / 60).toFixed(2)),
      },
    });

    const watchedLoggingStartedAtTime = watch('loggingStartedAtTime');
    const watchedLoggingStartedAtDate = watch('loggingStartedAtDate');
    const composedStartedAt = useMemo(() => {
      const isValidStartTime = enteredDateIsValid(watchedLoggingStartedAtTime ?? null);
      const isValidStartDate = enteredDateIsValid(watchedLoggingStartedAtDate ?? null);

      if (isValidStartTime && isValidStartDate) {
        return mergeDateAndTime(
          dayjs.tz(watchedLoggingStartedAtDate),
          dayjs.tz(watchedLoggingStartedAtTime),
        );
      }

      return null;
    }, [watchedLoggingStartedAtTime, watchedLoggingStartedAtDate]);

    const watchedLoggingEndedAtTime = watch('loggingEndedAtTime');
    const watchedLoggingEndedAtDate = watch('loggingEndedAtDate');
    const composedEndedAt = useMemo(() => {
      const isValidStartTime = enteredDateIsValid(watchedLoggingEndedAtTime ?? null);
      const isValidStartDate = enteredDateIsValid(watchedLoggingEndedAtDate ?? null);

      if (isValidStartTime && isValidStartDate) {
        return mergeDateAndTime(
          dayjs.tz(watchedLoggingEndedAtDate),
          dayjs.tz(watchedLoggingEndedAtTime),
        );
      }

      return null;
    }, [watchedLoggingEndedAtTime, watchedLoggingEndedAtDate]);

    const watchedApprovedWorkTimeHours = watch('approvedWorkTimeHours');
    const isValidApprovedWorkTimeHours = useMemo(() => {
      if (
        isNil(watchedApprovedWorkTimeHours) ||
        !String(watchedApprovedWorkTimeHours).trim().length
      ) {
        return false;
      }

      const asNumber = Number(watchedApprovedWorkTimeHours);

      return Number.isFinite(asNumber) && !Number.isNaN(asNumber);
    }, [watchedApprovedWorkTimeHours]);

    const calculatedTotalHrs = useMemo(() => {
      if (composedStartedAt && composedEndedAt) {
        return Number(composedEndedAt.diff(composedStartedAt, 'hour', true).toFixed(2));
      }

      return null;
    }, [composedStartedAt, composedEndedAt]);

    const shouldSuggestCalculatedHours = useMemo(() => {
      const startOrEndTimeChanged =
        dirtyFields.loggingStartedAtTime === true ||
        dirtyFields.loggingStartedAtDate === true ||
        dirtyFields.loggingEndedAtTime === true ||
        dirtyFields.loggingEndedAtDate === true;

      const newTotalHrsValueDiffersFromCalculated = isNil(calculatedTotalHrs)
        ? false
        : isValidApprovedWorkTimeHours
          ? calculatedTotalHrs !== Number(watchedApprovedWorkTimeHours)
          : true;

      return startOrEndTimeChanged && newTotalHrsValueDiffersFromCalculated;
    }, [
      calculatedTotalHrs,
      dirtyFields.loggingStartedAtTime,
      dirtyFields.loggingStartedAtDate,
      dirtyFields.loggingEndedAtTime,
      dirtyFields.loggingEndedAtDate,
      isValidApprovedWorkTimeHours,
      watchedApprovedWorkTimeHours,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              () => {
                resolve({
                  loggingStartedAt: composedStartedAt,
                  loggingEndedAt: composedEndedAt,
                  approvedWorkTimeMinutes: isValidApprovedWorkTimeHours
                    ? Math.round(Number(watchedApprovedWorkTimeHours) * 60)
                    : null,
                });
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
        prefill({ loggingStartedAt, loggingEndedAt, approvedWorkTimeHours }) {
          // Mirror how the form seeds its initial values so the tz handling matches.
          if (loggingStartedAt) {
            setValue('loggingStartedAtTime', dayjs.tz(loggingStartedAt).toDate(), {
              shouldDirty: true,
            });
            setValue('loggingStartedAtDate', dayjs.tz(loggingStartedAt).toDate(), {
              shouldDirty: true,
            });
          }
          if (loggingEndedAt) {
            setValue('loggingEndedAtTime', dayjs.tz(loggingEndedAt).toDate(), {
              shouldDirty: true,
            });
            setValue('loggingEndedAtDate', dayjs.tz(loggingEndedAt).toDate(), {
              shouldDirty: true,
            });
          }
          if (!isNil(approvedWorkTimeHours)) {
            setValue('approvedWorkTimeHours', approvedWorkTimeHours, {
              shouldDirty: true,
            });
          }
        },
      }),
      [
        composedEndedAt,
        composedStartedAt,
        handleSubmit,
        isValidApprovedWorkTimeHours,
        setValue,
        watchedApprovedWorkTimeHours,
      ],
    );

    useEffect(() => {
      clearErrors(['loggingStartedAtTime', 'loggingStartedAtDate']);

      if (!composedStartedAt) {
        setError('loggingStartedAtTime', { message: 'invalid' });
        setError('loggingStartedAtDate', { message: 'invalid' });
      }
    }, [composedStartedAt]);

    useEffect(() => {
      clearErrors(['loggingEndedAtTime', 'loggingEndedAtDate']);

      if (!composedEndedAt) {
        setError('loggingEndedAtTime', { message: 'invalid' });
        setError('loggingEndedAtDate', { message: 'invalid' });
      }
    }, [composedEndedAt]);

    // Report the live total up so an external headline can display it.
    useEffect(() => {
      onTotalHoursChange?.(
        isValidApprovedWorkTimeHours ? Number(watchedApprovedWorkTimeHours) : null,
      );
    }, [watchedApprovedWorkTimeHours, isValidApprovedWorkTimeHours, onTotalHoursChange]);

    // When the total input is hidden, adopt the calculated total as the user edits
    // Start/End (the common path). Skips the initial mount — nothing dirty yet — so a
    // saved manual override isn't clobbered on open. Also skips when the total itself
    // has been explicitly set (by the user or a prefill): an intentional total can
    // legitimately differ from the Start/End span, so we must not overwrite it.
    useEffect(() => {
      if (!hideTotalHoursInput) {
        return;
      }
      const startOrEndDirty =
        dirtyFields.loggingStartedAtTime === true ||
        dirtyFields.loggingStartedAtDate === true ||
        dirtyFields.loggingEndedAtTime === true ||
        dirtyFields.loggingEndedAtDate === true;
      const totalExplicitlySet = dirtyFields.approvedWorkTimeHours === true;
      if (startOrEndDirty && !totalExplicitlySet && calculatedTotalHrs !== null) {
        setValue('approvedWorkTimeHours', calculatedTotalHrs);
      }
    }, [
      calculatedTotalHrs,
      hideTotalHoursInput,
      dirtyFields.loggingStartedAtTime,
      dirtyFields.loggingStartedAtDate,
      dirtyFields.loggingEndedAtTime,
      dirtyFields.loggingEndedAtDate,
      dirtyFields.approvedWorkTimeHours,
      setValue,
    ]);

    return (
      <>
        <Container>
          <Label bold={captionLabels}>
            {captionLabels
              ? t('approvals.approve_jobs.summary_hero.start')
              : t('approvals.approve_jobs.grid_expanded_block.job_tab.job_start')}
          </Label>

          <SmallTimeInput
            control={control}
            isDisabled={isSubmittingForm}
            name="loggingStartedAtTime"
            suppressErrorMessage
          />

          <SmallDateInput
            control={control}
            isDisabled={isSubmittingForm}
            name="loggingStartedAtDate"
            suppressErrorMessage
          />

          <HelperText>{loggingStartedAtText}</HelperText>
        </Container>

        <Container>
          <Label bold={captionLabels}>
            {captionLabels
              ? t('approvals.approve_jobs.summary_hero.end')
              : t('approvals.approve_jobs.grid_expanded_block.job_tab.job_end')}
          </Label>

          <SmallTimeInput
            control={control}
            isDisabled={isSubmittingForm}
            name="loggingEndedAtTime"
            suppressErrorMessage
          />

          <SmallDateInput
            control={control}
            isDisabled={isSubmittingForm}
            name="loggingEndedAtDate"
            suppressErrorMessage
          />

          <HelperText>{loggingEndedAtText}</HelperText>
        </Container>

        {!hideTotalHoursInput && (
          <Container>
            <Label bold={captionLabels}>{t('approvals.driver_day.total_hrs')}</Label>

            <SmallNumberInput
              control={control}
              isDisabled={isSubmittingForm}
              name="approvedWorkTimeHours"
              suppressErrorMessage
            />

            {shouldSuggestCalculatedHours && (
              <PseudoLink
                title={`${t('approvals.approve_jobs.grid_expanded_block.job_tab.use_calculated')}: ${isNil(calculatedTotalHrs) ? '-' : `${calculatedTotalHrs}h`}`}
                action={() => {
                  if (!isNil(calculatedTotalHrs)) {
                    setValue('approvedWorkTimeHours', calculatedTotalHrs);
                  }
                }}
              />
            )}

            <HelperText>{approvedWorkTimeMinutesText}</HelperText>
          </Container>
        )}
      </>
    );
  },
);

export default JobSummaryEditForm;

function Container({ children }: React.PropsWithChildren) {
  return (
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>{children}</Box>
    </Box>
  );
}

function Label({
  bold,
  ...props
}: React.ComponentProps<typeof Typography> & { bold?: boolean }) {
  if (bold) {
    // Matches the v2 FormFields Label (ticket drawer): bold 10px caption, dark.
    return (
      <Typography
        {...props}
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '10px',
          fontWeight: 700,
        })}
      />
    );
  }
  return (
    <Typography
      {...props}
      variant="body2"
      sx={(theme) => ({ color: theme.brandV2.colors.treadGray2 })}
    />
  );
}

function HelperText(props: React.ComponentProps<typeof Typography>) {
  return (
    <Typography
      {...props}
      variant="caption"
      sx={(theme) => ({ color: theme.brandV2.colors.treadGray2, fontWeight: 500 })}
    />
  );
}

type StyledControllerInputProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  isDisabled?: boolean;
  name: FieldPath<TFieldValues>;
  suppressErrorMessage?: boolean;
};

function SmallTimeInput<TFieldValues extends FieldValues>(
  props: StyledControllerInputProps<TFieldValues>,
) {
  return (
    <TimeInput
      {...props}
      sx={{
        [`& .${inputBaseClasses.input}`]: { pl: 1, pr: 0, py: 0.5 },
        [`& .${buttonBaseClasses.root}`]: { p: 0.5 },
        [`& .${svgIconClasses.root}`]: { width: '18px', height: '18px' },
      }}
    />
  );
}

function SmallDateInput<TFieldValues extends FieldValues>(
  props: StyledControllerInputProps<TFieldValues>,
) {
  return (
    <DateInput
      {...props}
      sx={{
        [`& .${inputBaseClasses.input}`]: { pl: 1, pr: 0, py: 0.5 },
        [`& .${buttonBaseClasses.root}`]: { p: 0.5 },
        [`& .${svgIconClasses.root}`]: { width: '18px', height: '18px' },
      }}
    />
  );
}

function SmallNumberInput<TFieldValues extends FieldValues>(
  props: StyledControllerInputProps<TFieldValues>,
) {
  return (
    <NumberInput
      {...props}
      sx={{
        [`& .${inputBaseClasses.input}`]: {
          fontSize: '12px',
          height: '23px',
          px: 1,
          py: 0.5,
        },
      }}
    />
  );
}
